import { formatDate } from '@angular/common';
import { Component, inject, input, output, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { CancellationDocumentType } from 'src/app/_enums/cancellation/cancellation-document-type.enum';
import { CancellationFlowStatus } from 'src/app/_enums/cancellation/cancellation-flow-status.enum';
import {
  CancellationSendMethod,
  getCancellationSendMethodLabel,
  SELECTABLE_CANCELLATION_SEND_METHODS,
} from 'src/app/_enums/cancellation/cancellation-send-method.enum';
import { getInsuranceTypeLabel } from 'src/app/_enums/insurance-type.enum';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { pdfFile } from 'src/app/_helpers/custom.validators';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { APIResponse } from 'src/app/_types/api-responses.types';
import {
  CancellationFlowDetailsResponse,
  CancellationLetterPreviewRequest,
} from 'src/app/_types/cancellation.types';
import {
  nonBlank,
  richTextRequired,
} from 'src/app/Dashboard/customer/communications/compose/compose-validators';
import { FileInputComponent } from 'src/app/shared/components/file-input/file-input.component';
import { SlideInModalComponent } from 'src/app/shared/components/slide-in-modal/slide-in-modal.component';
import { EDITOR_PLUGINS, EDITOR_TOOLBAR } from 'src/app/shared/editor-config';
import { IconsModule } from 'src/app/shared/icons/icons.module';

/** Which of the two option cards is chosen. */
export type CancellationLetterMode = 'IMPORT' | 'GENERATE';

/**
 * « Résilier {affaire} »: the letter step of a cancellation. The operator
 * either has LTY write the letter — read it (« Imprimer »), mail it to the
 * customer to sign, or post it themselves — or files the letter the customer
 * already signed. Both leave through the same footer, whose buttons follow the
 * chosen option; mailing adds a step where the prose is read and edited before
 * it goes.
 *
 * Every route answers with the refreshed cancellation, handed up as `changed`
 * so the host repaints from one response. Opened by a host that owns the
 * cancellation, above it: `layer` stacks the sheet over that host's own.
 */
@Component({
  selector: 'app-cancellation-letter-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SlideInModalComponent,
    FileInputComponent,
    EditorComponent,
    IconsModule,
  ],
  templateUrl: './cancellation-letter-modal.component.html',
  styleUrl: './cancellation-letter-modal.component.css',
})
export class CancellationLetterModalComponent {
  private readonly fb = inject(FormBuilder);

  private readonly cancellationService = inject(CancellationService);

  private readonly toastr = inject(ToastrService);

  private readonly userAuthService = inject(UserAuthService);

  /** How many slide-ins this one opens above. */
  readonly layer = input(0);

  /** A route landed: the refreshed cancellation. */
  readonly changed = output<CancellationFlowDetailsResponse>();

  @ViewChild('modal')
  private readonly modal?: SlideInModalComponent;

  protected cancellation: CancellationFlowDetailsResponse | null = null;

  protected readonly form = this.fb.group({
    mode: new FormControl<CancellationLetterMode>('GENERATE', {
      nonNullable: true,
    }),
    sendMethod: new FormControl<CancellationSendMethod | null>(
      CancellationSendMethod.REGISTERED_LETTER,
      Validators.required
    ),
    letterDate: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    cancellationDate: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    file: new FormControl<File | null>(null),
    note: new FormControl('', { nonNullable: true }),
    subject: new FormControl('', { nonNullable: true, validators: nonBlank }),
    body: new FormControl('', { nonNullable: true }),
  });

  /** The form, or the mail's prose once « Envoyer par mail au client » was chosen. */
  protected step: 'form' | 'email' = 'form';

  /** TinyMCE lifecycle gate; see `ComposeModalBase.modalOpen`. */
  protected modalOpen = false;

  protected submitting = false;

  protected previewing = false;

  protected renderLoading = false;

  protected recipientEmail = '';

  protected readonly sendMethods = SELECTABLE_CANCELLATION_SEND_METHODS;

  protected readonly loadingText = $localize`Chargement…`;

  protected readonly getCancellationSendMethodLabel =
    getCancellationSendMethodLabel;

  protected readonly editorPlugins = EDITOR_PLUGINS;

  protected readonly editorToolbar = EDITOR_TOOLBAR;

  private readonly subscriptions = new Subscription();

  constructor() {
    this.subscriptions.add(
      this.form.controls.mode.valueChanges.subscribe(() =>
        this.applyModeValidators()
      )
    );
  }

  /**
   * Opens the sheet on a cancellation. The option offered first follows the
   * status: nothing generated yet means generating; a letter out means the
   * signed one is what comes next.
   */
  openModal(cancellation: CancellationFlowDetailsResponse): void {
    this.cancellation = cancellation;
    this.step = 'form';
    this.submitting = false;
    this.previewing = false;
    this.renderLoading = false;
    this.recipientEmail = '';
    this.form.reset({
      mode:
        cancellation.status === CancellationFlowStatus.LETTER_GENERATED
          ? 'IMPORT'
          : 'GENERATE',
      sendMethod:
        cancellation.sendMethod ?? CancellationSendMethod.REGISTERED_LETTER,
      letterDate: formatDate(new Date(), 'yyyy-MM-dd', 'en'),
      cancellationDate: cancellation.cancellationDate ?? '',
      file: null,
      note: '',
      subject: '',
      body: '',
    });
    this.applyModeValidators();
    this.modalOpen = true;
    this.modal?.openModal();
  }

  closeModal(): void {
    this.modal?.closeModal(false);
    this.modalOpen = false;
  }

  protected onClosed(): void {
    this.modalOpen = false;
  }

  protected get title(): string {
    const c = this.cancellation;
    return c?.baseInsuranceFlow?.displayId ?? c?.displayId ?? '';
  }

  /** « {n° de police} · {assureur} · {branche} », the policy being ended. */
  protected get policyLine(): string {
    const c = this.cancellation;
    if (!c) return '';
    return [
      c.policyNumber,
      c.insurer?.name,
      getInsuranceTypeLabel(c.insuranceType),
    ]
      .filter((part) => !!part)
      .join(' · ');
  }

  protected get mode(): CancellationLetterMode {
    return this.form.controls.mode.value;
  }

  /** What every route needs: how it will be sent, and both dates. */
  private get sendingValid(): boolean {
    const { sendMethod, letterDate, cancellationDate } = this.form.controls;
    return sendMethod.valid && letterDate.valid && cancellationDate.valid;
  }

  protected get canImport(): boolean {
    return (
      !this.submitting &&
      this.form.controls.sendMethod.valid &&
      this.form.controls.cancellationDate.valid &&
      !!this.form.controls.file.value &&
      this.form.controls.file.valid
    );
  }

  protected get canGenerate(): boolean {
    return !this.submitting && !this.previewing && this.sendingValid;
  }

  protected get canSendEmail(): boolean {
    return (
      !this.submitting &&
      !this.renderLoading &&
      this.form.controls.subject.valid &&
      richTextRequired(this.form.controls.body) === null
    );
  }

  /** The letter as it would be filed, in a tab of its own: the browser prints or saves it. */
  protected preview(): void {
    const id = this.cancellation?.id;
    if (!id || !this.canGenerate) return;
    this.previewing = true;
    this.subscriptions.add(
      this.cancellationService
        .previewLetter(id, this.letterRequest())
        .subscribe({
          next: (pdf) => {
            this.previewing = false;
            const url = URL.createObjectURL(pdf);
            window.open(url, '_blank');
            // Long enough for the tab to load it; a blob URL kept forever leaks the PDF.
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
          },
          error: (err) => {
            this.previewing = false;
            this.toastr.error(
              getApiErrorMessage(
                err,
                $localize`La lettre n'a pas pu être générée`
              )
            );
          },
        })
    );
  }

  protected sendByPost(): void {
    const id = this.cancellation?.id;
    if (!id || !this.canGenerate) return;
    this.run(
      this.cancellationService.generateLetterForPost(id, {
        ...this.letterRequest(),
        sendMethod: this.form.controls.sendMethod.value!,
      }),
      $localize`Lettre de résiliation générée`,
      (cancellation) => this.openFiledLetter(cancellation)
    );
  }

  /**
   * The letter « Enregistrer » just filed, in a tab of its own like « Voir »'s
   * draft: the newest generated letter among the refreshed aggregate's documents.
   */
  private openFiledLetter(cancellation: CancellationFlowDetailsResponse): void {
    const reference = cancellation.documents
      .filter(
        (document) =>
          document.type === CancellationDocumentType.CANCELLATION_LETTER
      )
      .sort((a, b) => a.createdOn.localeCompare(b.createdOn))
      .pop()?.url;
    if (!reference) return;
    if (reference.includes('https://')) {
      window.open(reference, '_blank');
      return;
    }
    const failed = () =>
      this.toastr.error($localize`La lettre n'a pas pu être ouverte`);
    this.subscriptions.add(
      this.userAuthService.getPresignedUrl(reference).subscribe({
        next: (res) =>
          res.status === 'SUCCESS'
            ? window.open(res.data.preSignedUrl, '_blank')
            : failed(),
        error: failed,
      })
    );
  }

  /** Prefills the prose from what the send would mail, then shows it. */
  protected openEmailStep(): void {
    const id = this.cancellation?.id;
    if (!id || !this.canGenerate) return;
    this.step = 'email';
    this.renderLoading = true;
    this.subscriptions.add(
      this.cancellationService
        .renderLetterEmail(id, this.letterRequest())
        .subscribe({
          next: (res) => {
            // A slow answer must not repopulate the next opening.
            if (!this.modalOpen) return;
            this.renderLoading = false;
            this.form.controls.subject.setValue(res.data.subject ?? '');
            this.form.controls.body.setValue(res.data.body ?? '');
            this.recipientEmail = res.data.recipientEmail ?? '';
          },
          error: (err) => {
            if (!this.modalOpen) return;
            this.renderLoading = false;
            this.step = 'form';
            this.toastr.error(
              getApiErrorMessage(
                err,
                $localize`L'e-mail n'a pas pu être préparé`
              )
            );
          },
        })
    );
  }

  protected backToForm(): void {
    this.step = 'form';
  }

  protected sendByEmail(): void {
    const id = this.cancellation?.id;
    if (!id || !this.canSendEmail) return;
    this.run(
      this.cancellationService.sendLetterByEmail(id, {
        ...this.letterRequest(),
        sendMethod: this.form.controls.sendMethod.value!,
        subject: this.form.controls.subject.value.trim(),
        body: this.form.controls.body.value,
      }),
      $localize`Lettre de résiliation envoyée au client`
    );
  }

  protected importSigned(): void {
    const id = this.cancellation?.id;
    if (!id || !this.canImport) return;
    this.run(
      this.cancellationService.importSignedLetter(id, {
        file: this.form.controls.file.value!,
        sendMethod: this.form.controls.sendMethod.value!,
        cancellationDate: this.form.controls.cancellationDate.value,
      }),
      $localize`Lettre signée importée`
    );
  }

  private letterRequest(): CancellationLetterPreviewRequest {
    const { letterDate, cancellationDate, note } = this.form.controls;
    return {
      letterDate: letterDate.value,
      cancellationDate: cancellationDate.value,
      note: this.mode === 'GENERATE' ? note.value.trim() || null : null,
    };
  }

  /** One route: the refreshed cancellation goes up, the sheet closes; an error keeps it open. */
  private run(
    request: Observable<APIResponse<CancellationFlowDetailsResponse>>,
    successMessage: string,
    onSuccess?: (cancellation: CancellationFlowDetailsResponse) => void
  ): void {
    this.submitting = true;
    this.subscriptions.add(
      request.subscribe({
        next: (res) => {
          this.submitting = false;
          this.toastr.success(successMessage);
          this.changed.emit(res.data);
          this.closeModal();
          onSuccess?.(res.data);
        },
        error: (err) => {
          this.submitting = false;
          this.toastr.error(
            getApiErrorMessage(
              err,
              $localize`Une erreur est survenue. Veuillez réessayer`
            )
          );
        },
      })
    );
  }

  /** The file is asked for only while it is what the operator is filing. */
  private applyModeValidators(): void {
    const { file } = this.form.controls;
    if (this.mode === 'IMPORT') {
      file.setValidators([Validators.required, pdfFile]);
    } else {
      file.clearValidators();
      file.setValue(null);
    }
    file.updateValueAndValidity();
  }
}
